import React, { useState } from 'react';
import { message } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { ActionBubbleButton } from './styled';
import { getActionIcon } from './icons';
import { SuggestedAction } from './types';
import { SaveAnalysisDrawer } from '../SaveAnalysis/SaveAnalysisDrawer';
import { ShareModal } from '../Share/ShareModal';
import ScheduleAnalysisDrawer from '../ScheduleSavedAnalysis/ScheduleAnalysisDrawer';
import { useNexaStore } from '../NexaStore';
import apptracker from '@src/utils/sensor/apptracker';
import { NEXA_ACTION_CLICK } from '@src/utils/sensor/track';
import { useUpdateSessionMetadata } from '../hooks/useUpdateSessionMetadata';

interface ActionBubbleProps {
  action: SuggestedAction;
  onOpenInputDialog?: (action: SuggestedAction) => void;
  onExecuteAgent?: (action: SuggestedAction) => void;
}

const ActionBubble: React.FC<ActionBubbleProps> = ({
  action,
  onOpenInputDialog,
  onExecuteAgent,
}) => {
  const IconComponent = getActionIcon(action.icon);
  const sessionId = useNexaStore((state) => state.id);
  const updateMetadataMutation = useUpdateSessionMetadata();

  // State for UI action modals
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [scheduleItem, setScheduleItem] = useState<{ id: number; query: string } | null>(null);

  const handleClick = () => {
    apptracker(NEXA_ACTION_CLICK, {
      action_id: action.action_id,
      label: action.label,
    });

    if (action.action_id === 'save_template') {
      setSaveModalOpen(true);
      return;
    }

    if (action.action_id === 'share_link') {
      setShareModalOpen(true);
      return;
    }

    if (action.action_id === 'pin_conversation') {
      if (sessionId) {
        updateMetadataMutation.mutate({ sessionId, is_pinned: true });
      } else {
        message.warning('No active session to pin');
      }
      return;
    }

    // Agent/hybrid/filter actions
    if (action.needs_input && onOpenInputDialog) {
      // Open input dialog for missing parameters
      onOpenInputDialog(action);
    } else if (onExecuteAgent) {
      // Execute action via BFF
      onExecuteAgent(action);
    } else {
      message.info(`Action ${action.action_id} execution not yet implemented`);
    }
  };

  return (
    <>
      <ActionBubbleButton onClick={handleClick} icon={<IconComponent />}>
        {action.label}
        {action.needs_input && <DownOutlined style={{ fontSize: 10, marginLeft: 2 }} />}
      </ActionBubbleButton>

      {/* Save Analysis Drawer */}
      {sessionId && (
        <SaveAnalysisDrawer
          open={saveModalOpen}
          onClose={() => setSaveModalOpen(false)}
          sessionId={sessionId}
          onSaveAndSchedule={setScheduleItem}
        />
      )}

      <ScheduleAnalysisDrawer
        open={!!scheduleItem}
        onClose={() => setScheduleItem(null)}
        analysisItem={scheduleItem}
        mode="create"
      />

      {/* Share Modal */}
      {sessionId && (
        <ShareModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          sessionId={sessionId}
        />
      )}
    </>
  );
};

export default ActionBubble;
